/**
 * r7-25 — acesso por plano no lado da SPA.
 *
 * Quem decide e o backend (`User#plan_access`, devolvido em `/auth/me`). Este modulo
 * so LE esse snapshot para (a) mandar quem nao tem plano para a tela "sem plano",
 * (b) esconder do menu o que o plano nao libera e (c) trocar a rota de uma feature
 * fora do plano por um aviso. A API recusa do mesmo jeito (402/403) — a tela e so
 * a traducao disso.
 *
 * Modulo puro (sem JSX) para ser testavel e importavel por layout, menu e rotas.
 */
import type { PlanFeatureKey } from '@/types/api'
import type { User } from '@/types/user'
import { MANUAL_ROUTE } from '@/lib/copy/manual'
import { ANALYSIS_SECTION } from '@/lib/copy/analysis'
import { EXAM_PROCESSES_ROUTE } from '@/lib/copy/examProcesses'

/** Tela "nenhum plano vinculado", fora de `/app` (sem menu). */
export const NO_PLAN_PATH = '/sem-plano'

/**
 * Rotas de `/app` que continuam abertas sem plano: sao as telas cujo backend esta na
 * allowlist do gate (`SubscriptionGateAllowlist`) — configuracoes/perfil e a tela de
 * assinatura legada.
 */
const OPEN_WITHOUT_PLAN = ['/app/configuracoes', '/app/assinatura'] as const

/**
 * Prefixo de rota -> feature. A primeira entrada que casar vence, entao prefixos mais
 * especificos vem antes. Rota fora da tabela nao exige feature.
 */
export const ROUTE_FEATURES: ReadonlyArray<readonly [string, PlanFeatureKey]> = [
  ['/app/dashboard', 'dashboard'],
  ['/app/jornada', 'jornada'],
  ['/app/calendario', 'calendario'],
  ['/app/meta-ted', 'meta_ted'],
  [EXAM_PROCESSES_ROUTE, 'central_do_aprovado'],
  [MANUAL_ROUTE, 'registro_estudos'],
  ['/app/analise-metricas', 'analise_metricas'],
  [ANALYSIS_SECTION.path, 'analise_estatistica'],
  ['/app/provas', 'provas_integra'],
  ['/app/simulados-integracoes', 'integracoes'],
  ['/app/simulados', 'simulados'],
  ['/app/desempenho', 'simulados'],
  ['/app/video-aulas', 'video_aulas'],
  ['/app/aulas-ao-vivo', 'aulas_ao_vivo'],
  ['/app/relato-de-dificuldades', 'relato_dificuldades'],
  ['/app/social/match-hunter', 'match_hunter'],
  ['/app/social/friends', 'match_hunter'],
  ['/app/social/conversations', 'conversas'],
  ['/app/social/chat', 'conversas'],
  ['/app/integracoes', 'integracoes'],
  ['/app/estudos', 'integracoes'],
  ['/app/revisoes-recomendadas', 'integracoes'],
  ['/app/revisoes', 'integracoes'],
  ['/app/prevalencia', 'integracoes'],
]

function matches(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`)
}

/** A feature que a rota exige, ou `null`. */
export function routeFeature(pathname: string): PlanFeatureKey | null {
  const hit = ROUTE_FEATURES.find(([prefix]) => matches(pathname, prefix))
  return hit ? hit[1] : null
}

/**
 * `true` quando o usuario precisa ir para a tela "sem plano". Sem `plan_access` no
 * payload (API anterior a r7-25 ou `user` velho do localStorage) nada e bloqueado:
 * o proximo `/auth/me` corrige, e o servidor recusa de qualquer jeito.
 */
export function needsPlan(user: User | null): boolean {
  if (!user?.plan_access) return false
  return !user.plan_access.has_access
}

export function isOpenWithoutPlan(pathname: string): boolean {
  return OPEN_WITHOUT_PLAN.some((prefix) => matches(pathname, prefix))
}

/**
 * Decide o redirecionamento para `NO_PLAN_PATH`: sem plano (pelo `/auth/me`) ou com o
 * 402 do gate ja recebido, exceto nas rotas abertas sem plano.
 */
export function shouldRedirectToNoPlan(user: User | null, blocked: boolean, pathname: string): boolean {
  if (!blocked && !needsPlan(user)) return false
  return !isOpenWithoutPlan(pathname)
}

/** Sem `plan_access` (API antiga), tudo liberado — ver `needsPlan`. */
export function hasFeature(user: User | null, feature: PlanFeatureKey): boolean {
  const access = user?.plan_access
  if (!access) return true
  if (access.full_access) return true
  return access.features.includes(feature)
}

/**
 * O plano pede que a feature fora dele apareca no menu com cadeado, em vez de sumir.
 * Acesso total nao tem o que bloquear; sem o campo (API/`user` antigo), vale `hide`.
 */
export function showsLockedFeatures(user: User | null): boolean {
  const access = user?.plan_access
  if (!access || access.full_access) return false
  return access.show_locked_features === true
}

/** A feature que falta para abrir a rota, ou `null` se a rota esta liberada. */
export function missingRouteFeature(user: User | null, pathname: string): PlanFeatureKey | null {
  const feature = routeFeature(pathname)
  if (!feature) return null
  return hasFeature(user, feature) ? null : feature
}
